import { createAsyncThunk } from '@reduxjs/toolkit';
import { omit } from 'lodash-es';

import { log } from '../../../services/logging';
import migrate from '../../../services/migrate';
import { trackSceneLoad } from '../../../services/tracking';
import { isVersion5OorGreater } from '../../../services/versions/versionUtils';
import type { Scene } from '../../../types/scene';
import { getServerVersion, getUserId } from '../../connections/connectionsDuck';
import { setFilterRules } from '../../filter/filter';
import { setGdsState } from '../../gds/gds';
import { addToInventory, addToVisible } from '../../graph/graph.actions';
import { addNodeGroups } from '../../graph/nodes';
import { getVisibleLabels, getVisibleRelationshipTypes } from '../../perspectives/perspectives';
import { getPerspective } from '../../perspectives/perspectives.selector';
import { clearState } from '../../rootReducer';
import { getEnableCytoscape } from '../../settings/settings';
import { setSlicerRanges } from '../../slicer/slicer';
import { mapPerspectiveToStyle } from '../../styles/styleMiddleware';
import { setCurrentStyleId } from '../../styles/styles';
import { selectStyleById } from '../../styles/styles.selectors';
import type { RootState } from '../../types';
import {
  getLayoutType,
  setLayoutOptions,
  setLayoutType,
  setPanCoordinates,
  setZoomLevel,
} from '../../visualization/visualization';
import { trasformations } from '../migrations';
import { setCurrentNodePositions, setCurrentSceneId, setEditMode, setSceneLoadingStatus, updateScene } from '../scene';
import { SCENE_EDIT_MODE_EDITING, SCENE_EDIT_MODE_VIEWING } from '../scene.selectors';
import {
  actualiseNodeAndRelationshipIds,
  getCombinedNodeIdsWithGrouped,
  getStyleRulesCount,
  loadNodesAndRelationships,
} from '../sceneActions';
import { NAME } from './const';
import { selectSceneThunk } from './select-scene';

type ApplySelectedScenePayload = {
  sceneId: string;
  trackingEnabled?: boolean;
  viaLink?: boolean;
  isGraphProvided?: boolean;
};

export const applySelectedSceneThunk = createAsyncThunk<void, ApplySelectedScenePayload, { state: RootState }>(
  `${NAME}/applySelectedSceneThunk`,
  async ({ sceneId, trackingEnabled = false, viaLink = false, isGraphProvided = false }, { dispatch, getState }) => {
    const state = getState();
    const perspective = getPerspective(state);
    if (!perspective) return;

    const userId = getUserId(state);
    const perspectiveStyle = selectStyleById(state)(perspective.id);
    if (!userId || !perspectiveStyle) return;

    dispatch(setSceneLoadingStatus('loading'));
    const { selectedScene: sceneToApply, isTemporaryScene } = await dispatch(
      selectSceneThunk({ sceneId, isGraphProvided }),
    ).unwrap();

    if (!sceneToApply) {
      // Is not saved on the db yet
      // populate with perspective style
      dispatch(setCurrentSceneId(sceneId));
      dispatch(setCurrentStyleId(sceneId));

      dispatch(clearState());
      dispatch(setCurrentNodePositions([]));
      dispatch(setSceneLoadingStatus('idle'));
      if (isTemporaryScene) {
        dispatch(setEditMode(SCENE_EDIT_MODE_EDITING));
      }
      return;
    }

    const enableCytoscape = getEnableCytoscape(state);
    const isV5OrGreater = isVersion5OorGreater(getServerVersion(state)) ?? false;
    const visibleRelationshipTypes = getVisibleRelationshipTypes(state);
    const visibleLabels = getVisibleLabels(state);
    const layoutType = getLayoutType(state);

    const migratedScene: Scene = migrate(
      sceneToApply,
      trasformations(perspectiveStyle, enableCytoscape),
      sceneToApply?.version,
    );

    const {
      id,
      createdBy,
      nodes,
      relationships: visibleRelationshipIds,
      nodeGroups = {},
      visualisation,
      filters = [],
      gds,
      style,
      ranges = [],
    } = migratedScene;
    const sceneForReducer = omit(migratedScene, [
      'nodes',
      'relationships',
      'visualisation',
      'filters',
      'gds',
      'style',
      'ranges',
    ]);

    dispatch(setCurrentSceneId(id));

    if (!isTemporaryScene) {
      dispatch(updateScene(sceneForReducer));
    }

    if (style !== undefined) {
      setTimeout(() => {
        mapPerspectiveToStyle(perspective, style, dispatch);
      }, 0);
    }

    if (trackingEnabled) {
      const numNodes = nodes?.length ?? 0;
      const numRelationships = visibleRelationshipIds?.length ?? 0;
      trackSceneLoad(
        id,
        perspective.id,
        numNodes,
        numRelationships,
        style !== undefined ? getStyleRulesCount(style) : 0,
        viaLink,
      );
    }

    if (!nodes) {
      dispatch(setSceneLoadingStatus('idle'));
      return;
    }

    const visibleNodeIds = getCombinedNodeIdsWithGrouped(nodes, nodeGroups);
    const { nodes: inventoryNodes, relationships: inventoryRelationships } = await loadNodesAndRelationships(
      visibleNodeIds,
      visibleRelationshipIds ?? [],
      perspective,
      visibleRelationshipTypes,
      visibleLabels,
      isV5OrGreater,
    ).catch((error) => {
      log.error('Failed to load nodes and relationships:', error);
      dispatch(setSceneLoadingStatus('idle'));
      return { nodes: [], relationships: [] };
    });

    const { actualVisibleNodeIds, actualVisibleRelationshipIds, actualPositionNodes } = actualiseNodeAndRelationshipIds(
      inventoryNodes,
      inventoryRelationships,
      visibleRelationshipIds ?? [],
      nodes,
      isV5OrGreater,
    );

    dispatch(clearState());

    const setVisualization = async () => {
      if (!visualisation) {
        return;
      }
      dispatch(setZoomLevel(visualisation.zoomLevel));
      if (visualisation.panCoordinates) {
        dispatch(setPanCoordinates(visualisation.panCoordinates));
      }
      if (layoutType !== visualisation.layoutType) {
        dispatch(setLayoutType(visualisation.layoutType));
      }
      if (visualisation.layoutOptions) {
        dispatch(setLayoutOptions(visualisation.layoutOptions));
      }
    };

    const setInventory = () =>
      new Promise<void>((resolve) => {
        const newSceneRanges = ranges;
        setTimeout(() => {
          dispatch(
            addToInventory({
              nodes: inventoryNodes,
              relationships: inventoryRelationships,
            }),
          );
          dispatch(
            addToVisible({
              nodeIds: actualVisibleNodeIds,
              relationshipIds: actualVisibleRelationshipIds,
              categories: perspective.categories,
            }),
          );
          dispatch(setFilterRules(filters));
          dispatch(setSlicerRanges(newSceneRanges));
          if (gds) {
            dispatch(setGdsState(gds));
          }
          dispatch(addNodeGroups(nodeGroups));
          resolve();
        }, 0);
      });

    const setPositions = () =>
      new Promise<void>((resolve) => {
        setTimeout(() => {
          dispatch(setCurrentNodePositions(actualPositionNodes));
          dispatch(setSceneLoadingStatus('inventory'));
          resolve();
        }, 0);
      });

    visualisation && (await setVisualization());
    await setInventory();

    dispatch(setCurrentStyleId(id));

    const isUserSceneOwner = createdBy === userId;
    dispatch(setEditMode(isUserSceneOwner ? SCENE_EDIT_MODE_EDITING : SCENE_EDIT_MODE_VIEWING));

    await setPositions();
  },
);
